import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, Button, Card, ConfirmationModal, Page, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useHistoryLength } from '~/hooks/useNavigationBlocker';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { integrationModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { PracticeManagement, isPracticeManagementIntegrationItem } from '~/types/api.model';
import { getPMDetails } from '../pm.utils';
import { PMMapServicesModal } from '../PMMapServicesModal/PMMapServicesModal';
import PMMapServicesModalOld from '../PMMapServicesModal/PMMapServicesModalOld';
import styles from './PMSetupPage.module.scss';
export const GetPMSetupPageTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            PMSetupPageTID: {
                MapServices: (testId)=>testId ? "".concat(testId, "-map-services") : '',
                Disconnect: (testId)=>testId ? "".concat(testId, "-disconnect") : '',
                ConfirmDisconnectModal: 'confirm-disconnect-modal',
                DefaultWorkAssignee: 'default-work-assignee'
            }
        }
    });
export default function PMSetupPage(props) {
    const { SEAGULL_TABLES: isSeagullTablesEnabled } = useFeatureFlags();
    const ServicesModal = isSeagullTablesEnabled === true ? PMMapServicesModal : PMMapServicesModalOld;
    const { pmName, testId, integration, getItems, doDisconnect, linkServiceTemplate, getServiceTemplateIntegratedIds } = props;
    const navigate = useNavigate();
    const historyLength = useHistoryLength();
    const { fetchIntegrations, fetchKarbonIntegration, fetchKarbonUsers, api } = useStoreActions();
    const { showModal, showErrorToast, showSuccessToast, startLoader, stopLoader } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: pmSetupPageTexts } = useLocaleKeys('integrations.setup.practiceManagementPage');
    const karbonUsers = useStore(selectors.integration.karbonUsers);
    const { PMSetupPageTID } = useMemo(()=>GetPMSetupPageTID(testId), [
        testId
    ]);
    const renderCard = (text, action)=>/*#__PURE__*/ _jsx(Card, {
            bi: {
                name: 'pm_setup_card'
            },
            autoHeight: true,
            padding: "m",
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.cardContent,
                children: [
                    text,
                    action
                ]
            })
        });
    useOnMount(()=>{
        if (pmName === PracticeManagement.Karbon) handlePromise(fetchKarbonUsers());
    });
    async function disconnect() {
        const confirmed = await showModal({
            biName: 'pm_setup_page_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                const cancel = ()=>dismissModal(false);
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'p_m_setup_confirm'
                        },
                        secondaryButton: {
                            name: 'p_m_setup_cancel'
                        }
                    },
                    testId: PMSetupPageTID.ConfirmDisconnectModal(),
                    skin: "danger",
                    title: pmSetupPageTexts.confirmDisconnectTitle({
                        pmName: getPMDetails(pmName).name
                    }),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: pmSetupPageTexts.confirmDisconnectContent({
                            pmName: getPMDetails(pmName).name
                        })
                    }),
                    primaryButtonLabel: commonTexts.buttons.disconnect(),
                    secondaryButtonLabel: commonTexts.buttons.cancel(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: cancel,
                    onCloseButtonClick: cancel
                });
            }
        });
        if (!confirmed) return;
        const disconnectLoader = 'disconnect';
        startLoader(disconnectLoader);
        const result = await handlePromise(doDisconnect());
        if (result.success) {
            await handlePromise(fetchIntegrations());
            goBack();
        } else {
            showErrorToast(commonTexts.toasts.failedToDisconnect(), 'failed-to-disconnect-toast');
        }
        stopLoader(disconnectLoader);
    }
    async function mapServices() {
        showModal({
            biName: 'pm_map_services_modal',
            content: (p)=>/*#__PURE__*/ _jsx(ServicesModal, _object_spread({
                    pmName: pmName,
                    getItems: getItems,
                    linkServiceTemplate: linkServiceTemplate,
                    getServiceTemplateIntegratedIds: getServiceTemplateIntegratedIds
                }, p))
        });
    }
    function goBack() {
        if (historyLength > 1) {
            navigate(-1);
        } else {
            navigate(routes.availableApps);
        }
    }
    return /*#__PURE__*/ _jsx(Page, {
        testId: testId,
        header: pmSetupPageTexts.integrationSetup({
            pmName: getPMDetails(pmName).name
        }),
        subheader: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                pmSetupPageTexts.setupSubheader({
                    pmName: getPMDetails(pmName).name
                }),
                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                    openInNewTab: true,
                    href: getPMDetails(pmName).articleLink,
                    underline: "none",
                    bi: {
                        name: 'pm_setup_learn_more'
                    },
                    children: "Learn more"
                })
            ]
        }),
        onClickBack: goBack,
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.cards,
            children: [
                renderCard(/*#__PURE__*/ _jsx(Typography, {
                    children: pmSetupPageTexts.mapServicesDescription({
                        pmName: getPMDetails(pmName).name
                    })
                }), /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'map_pm_services'
                    },
                    testId: testId && PMSetupPageTID.MapServices(testId),
                    className: styles.actionButton,
                    onClick: mapServices,
                    children: commonTexts.buttons.mapServices()
                })),
                pmName === PracticeManagement.Karbon && karbonUsers && integration && integrationModel.isLinked(integration) && renderCard(/*#__PURE__*/ _jsx(Typography, {
                    children: pmSetupPageTexts.setDefaultAssignee()
                }), /*#__PURE__*/ _jsx(Autocomplete, {
                    bi: {
                        name: 'pm_default_assignee'
                    },
                    selectOnFocus: true,
                    disableClearable: true,
                    multiple: false,
                    size: "small",
                    testId: PMSetupPageTID.DefaultWorkAssignee(),
                    options: karbonUsers,
                    getOptionLabel: (option)=>option.name,
                    value: karbonUsers.find((user)=>user.id === integrationModel.karbonDefaultAssignee(integration)),
                    textFieldProps: {
                        minWidth: '240px'
                    },
                    onChange: async (e, value)=>{
                        if (!value || !isPracticeManagementIntegrationItem(value)) return;
                        const result = await handlePromise(api.karbon.updateKarbonIntegration(value.id));
                        if (result.success) {
                            await fetchKarbonIntegration();
                            showSuccessToast(commonTexts.toasts.defaultAssigneeUpdatedSuccessfully(), "karbon-assignee-success-toast");
                        } else {
                            showErrorToast(commonTexts.toasts.failedToUpdateDefaultAssignee(), "karbon-assignee-error-toast");
                        }
                    }
                })),
                renderCard(/*#__PURE__*/ _jsx(Typography, {
                    skin: "error",
                    children: pmSetupPageTexts.disconnectFrom({
                        pmName: getPMDetails(pmName).name
                    })
                }), /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'disconnect_pm'
                    },
                    testId: testId && PMSetupPageTID.Disconnect(testId),
                    className: styles.actionButton,
                    skin: "danger",
                    onClick: disconnect,
                    children: commonTexts.buttons.disconnect()
                }))
            ]
        })
    });
}
